import React, { useState, useMemo, useEffect, useRef } from "react";
import { 
  HiOutlineSearch, 
  HiViewGrid, 
  HiViewList, 
  HiPlus, 
  HiOutlineEmojiSad,
  HiChevronDown,
  HiOutlineFilter,
  HiFolderAdd,
  HiChevronRight,
  HiHome,
  HiCloudUpload
} from "react-icons/hi";
import { BsGrid3X3GapFill } from "react-icons/bs";
import { motion, AnimatePresence } from "framer-motion";
import FileCard from "../Components/FileCard";
import FolderCard from "../Components/FolderCard";
import CreateFolderModal from "../Components/CreateFolderModal";
import ViewFileModal from "../Components/ViewFileModal";
import RenameModal from "../Components/RenameModal";
import ShareModal from "../Components/ShareModal";
import { 
  getFoldersAPI, 
  getFolderContentAPI, 
  uploadFileAPI, 
  renameFileAPI,
  grantAccessAPI 
} from "../services/driveService";
import { toast } from "react-toastify";

export default function Homepage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("grid"); // 'list', 'grid', 'big-grid'
  const [activeCategory, setActiveCategory] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const fileInputRef = useRef(null);
  
  // Navigation State
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Home" }]);
  
  // Real Data State
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      if (currentFolderId === null) {
        const data = await getFoldersAPI();
        setFolders(data || []);
        setFiles([]); 
      } else {
        const data = await getFolderContentAPI(currentFolderId);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      }
    } catch (error) {
      toast.error("Failed to load content");
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
    const handleRefresh = () => fetchData();
    window.addEventListener("folderCreated", handleRefresh);
    return () => window.removeEventListener("folderCreated", handleRefresh);
  }, [currentFolderId]);

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (id, index) => {
    setCurrentFolderId(id);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const toastId = toast.loading(`Uploading ${file.name}...`);
    
    try {
      await uploadFileAPI(file, currentFolderId);
      toast.update(toastId, { 
        render: "Uploaded successfully!", 
        type: "success", 
        isLoading: false, 
        autoClose: 3000 
      });
      fetchData();
    } catch (error) {
      toast.update(toastId, { 
        render: error?.message || "Upload failed", 
        type: "error", 
        isLoading: false, 
        autoClose: 3000 
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRename = async (newName) => {
    if (!renamingItem) return;
    try {
      await renameFileAPI(renamingItem.id, newName);
      toast.success("Renamed successfully!");
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Rename failed");
    }
  };

  const handleShareSubmit = async (userId, permission) => {
    if (!sharingItem) return;
    try {
      // Determine resource type (File or Folder)
      // Note: backend expects lowercase usually, check curl
      const resourceType = sharingItem.extension ? "File" : "Folder";
      await grantAccessAPI(userId, sharingItem.id, resourceType, permission);
      toast.success(`Access granted to user for ${sharingItem.name}`);
      setSharingItem(null);
    } catch (error) {
      toast.error(error?.message || "Failed to grant access");
    }
  };

  const categories = [
    { id: "all", label: "All Files" },
    { id: "document", label: "Documents" },
    { id: "image", label: "Images" },
    { id: "video", label: "Videos" },
  ];

  const filteredFiles = useMemo(() => {
    return files.filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const contentType = file.contentType || "";
      const extension = (file.extension || file.name.split('.').pop()).toLowerCase();
      
      const isDoc = contentType.includes("pdf") || contentType.includes("word") || ["pdf", "doc", "docx", "txt"].includes(extension);
      const isImg = contentType.includes("image") || ["jpg", "jpeg", "png", "gif", "svg"].includes(extension);
      const isVid = contentType.includes("video") || ["mp4", "mkv", "avi"].includes(extension);

      if (activeCategory === "document") return isDoc && matchesSearch;
      if (activeCategory === "image") return isImg && matchesSearch;
      if (activeCategory === "video") return isVid && matchesSearch;
      
      return matchesSearch;
    });
  }, [searchQuery, activeCategory, files]);

  return (
    <div className="mx-auto space-y-8 pb-32 px-4 md:px-0">
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileUpload} 
        className="hidden" 
      />

      {/* Search Header */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative group flex-1 max-w-2xl">
          <HiOutlineSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 text-2xl group-focus-within:text-indigo-500 transition-colors" />
          <input
            type="text"
            placeholder="Search in DriveHub..."
            className="w-full pl-16 pr-6 py-4 bg-white/70 backdrop-blur-xl border border-white shadow-2xl shadow-indigo-100/20 rounded-[2rem] outline-none focus:ring-4 focus:ring-indigo-500/10 focus:bg-white transition-all text-lg font-medium placeholder:text-slate-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-indigo-600 border border-indigo-100 px-6 py-4 rounded-3xl shadow-lg shadow-indigo-100 transition-all active:scale-95 font-bold"
          >
            <HiFolderAdd className="text-xl" />
            <span className="hidden sm:inline">New Folder</span>
          </button>
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-8 py-4 rounded-3xl shadow-lg shadow-indigo-200 transition-all active:scale-95 font-bold"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <HiCloudUpload className="text-xl" />
            )}
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>

      {/* Breadcrumbs & Universal View Toggle */}
      <div className="flex items-center justify-between py-2 border-b border-slate-200/50">
        <div className="flex items-center gap-2 text-sm overflow-x-auto no-scrollbar py-2">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id || "root"}>
              <button
                onClick={() => handleBreadcrumbClick(crumb.id, index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-2xl transition-all font-bold whitespace-nowrap shadow-sm border ${
                  index === breadcrumbs.length - 1 
                  ? "bg-white text-indigo-600 border-indigo-100 shadow-indigo-100/50" 
                  : "bg-white/50 text-slate-400 border-white hover:bg-white hover:text-indigo-500"
                }`}
              >
                {index === 0 && <HiHome size={16} />}
                {crumb.name}
              </button>
              {index < breadcrumbs.length - 1 && (
                <HiChevronRight className="text-slate-300" />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="flex bg-white/40 p-1 rounded-2xl border border-white/60 shadow-inner gap-1 min-w-fit ml-4">
          <button
            onClick={() => setViewType("list")}
            title="List View"
            className={`p-2 rounded-xl transition-all duration-300 ${
              viewType === "list" ? "bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600"
            }`}
          >
            <HiViewList className="text-xl" />
          </button>
          <button
            onClick={() => setViewType("grid")}
            title="Grid View"
            className={`p-2 rounded-xl transition-all duration-300 ${
              viewType === "grid" ? "bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600"
            }`}
          >
            <HiViewGrid className="text-xl" />
          </button>
          <button
            onClick={() => setViewType("big-grid")}
            title="Big Grid View"
            className={`p-2 rounded-xl transition-all duration-300 ${
              viewType === "big-grid" ? "bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600"
            }`}
          >
            <BsGrid3X3GapFill className="text-xl" />
          </button>
        </div>
      </div>

      {/* Categories - Only show inside a folder */}
      {currentFolderId && (
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-2.5 rounded-2xl text-sm font-bold transition-all border ${
                activeCategory === cat.id 
                  ? "bg-white text-indigo-600 border-indigo-100 shadow-lg shadow-indigo-100/50" 
                  : "bg-white/50 text-slate-400 border-white hover:bg-white hover:text-slate-600"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-12">
        {/* Folders Section */}
        {!searchQuery && (
          <section className="animate-in fade-in slide-in-from-left-4 duration-1000">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                {currentFolderId ? "Sub-folders" : "Folders"}
              </h3>
            </div>
            
            {loadingData ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                 {[1,2,3,4].map(i => (
                   <div key={i} className="h-20 bg-white/50 animate-pulse rounded-2xl border border-white" />
                 ))}
               </div>
            ) : (
              <div className={`
                ${viewType === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" : ""}
                ${viewType === "big-grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" : ""}
                ${viewType === "list" ? "flex flex-col gap-2" : ""}
              `}>
                <AnimatePresence mode="popLayout">
                  {folders.map((folder) => (
                    <FolderCard 
                      key={folder.id} 
                      folder={folder} 
                      view={viewType === "list" ? "list" : "grid"}
                      isBig={viewType === "big-grid"}
                      onClick={() => handleFolderClick(folder)}
                      onRename={setRenamingItem}
                      onShare={setSharingItem}
                    />
                  ))}
                </AnimatePresence>
                {folders.length === 0 && !loadingData && (
                  <div className="col-span-full py-10 text-center bg-white/30 rounded-3xl border-2 border-dashed border-white/50">
                    <p className="text-slate-400 font-medium italic">No folders here</p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Files Section */}
        {currentFolderId && (
          <section className="animate-in fade-in slide-in-from-right-4 duration-1000 delay-150">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                Files
              </h3>
            </div>
          
            {loadingData ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-40 bg-white/50 animate-pulse rounded-2xl border border-white" />
                ))}
              </div>
            ) : (
              <div className={`
                ${viewType === "grid" ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6" : ""}
                ${viewType === "big-grid" ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" : ""}
                ${viewType === "list" ? "flex flex-col gap-2" : ""}
              `}>
                <AnimatePresence mode="popLayout">
                  {filteredFiles.map((file) => {
                    const extension = file.extension || file.name.split('.').pop();
                    return (
                      <FileCard 
                        key={file.id} 
                        file={{...file, extension}} 
                        view={viewType === "list" ? "list" : "grid"} 
                        isBig={viewType === "big-grid"}
                        onView={() => setViewingFile(file)}
                        onRename={setRenamingItem}
                        onShare={setSharingItem}
                      />
                    );
                  })}
                </AnimatePresence>
                
                {filteredFiles.length === 0 && !loadingData && (
                  <div className="col-span-full py-20 text-center rounded-[3rem] bg-white/20 border-2 border-dashed border-white/40">
                    <div className="flex flex-col items-center gap-4 text-slate-400">
                      <HiOutlineEmojiSad size={48} className="opacity-20" />
                      <p className="font-bold">No files found here</p>
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="text-indigo-600 text-sm hover:underline font-bold"
                      >
                        Upload your first file
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      <CreateFolderModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchData()}
        parentId={currentFolderId}
      />

      <ViewFileModal 
        file={viewingFile}
        isOpen={!!viewingFile}
        onClose={() => setViewingFile(null)}
      />

      <RenameModal 
        isOpen={!!renamingItem}
        onClose={() => setRenamingItem(null)}
        currentName={renamingItem?.name}
        onRename={handleRename}
      />

      <ShareModal 
        isOpen={!!sharingItem}
        onClose={() => setSharingItem(null)}
        resource={sharingItem}
        onShare={handleShareSubmit}
      />
    </div>
  );
}
